import React from 'react';
import PropTypes from 'prop-types';

import { Avatar, Box, IconButton } from '@mui/material';

import FavoriteHeartIcon from '../../../../assets/icons/FavoriteHeartIcon';
import WorkerNameLink from '../../../../components/WorkerNameLink/WorkerNameLink';
import { BLACK, RED } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import useFairReliabilityScore from '../../../../utils/useFairReliabilityScore';

import AlsTooltipContent from './AlsTooltipContent';
import latestGpsStatus from './latestGpsStatus';
import ReliabilityScoreBadge from './ReliabilityScoreBadge';
import RosterMetaRow from './RosterMetaRow';
import WorkerReport from './WorkerReport';

const RosterCardContainer = ({
  isFavorite,
  reliabilityScore,
  rawReliability,
  rawAls,
  isPriorityResourcePool,
  firstTimeWorkingThisCustomer,
  gpsData,
  gpsPings,
  children,
  employeeName,
  employeeId,
  agencyId,
  profilePictureSrc,
  avatarAlt,
  dataTestId,
  isWorkerReportOpen,
  onAcceptApplication,
  onDeclineApplication,
  onFavoriteClick,
  employeePhoneNumber,
}) => {
  const gpsStatus = latestGpsStatus({ gpsData, gpsPings });
  const { pickReliability, canSeeAls } = useFairReliabilityScore();
  const displayedReliabilityScore = pickReliability(rawReliability, reliabilityScore);
  const alsTooltip = canSeeAls ? <AlsTooltipContent score={rawAls} /> : undefined;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        minHeight: '72px',
        borderRadius: '12px',
        border: `1px solid ${BLACK[40]}`,
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap="12px"
        padding="12px 24px"
        justifyContent="space-between"
        width="100%"
        height="100%"
      >
        <Box display="flex" alignItems="center" gap="12px" minWidth={0}>
          <IconButton
            onClick={onFavoriteClick}
            size="small"
            sx={{
              padding: '4px',
              cursor: 'pointer',
              '&:hover svg': {
                color: RED[80],
              },
            }}
          >
            <FavoriteHeartIcon
              isFavorite={isFavorite}
              activeColor={RED[70]}
              inactiveColor={BLACK[40]}
              height={24}
              width={24}
            />
          </IconButton>
          <ReliabilityScoreBadge score={displayedReliabilityScore} tooltip={alsTooltip}>
            <Avatar alt={avatarAlt ?? employeeName ?? ''} src={profilePictureSrc} />
          </ReliabilityScoreBadge>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginLeft: '10px',
              paddingLeft: '8px',
            }}
          >
            <WorkerNameLink
              name={employeeName ?? ''}
              userId={employeeId}
              agencyId={agencyId}
              sx={{
                fontFamily: PRIMARY_FONT[500],
                fontSize: '16px',
              }}
            />
            <RosterMetaRow
              isPriorityResourcePool={isPriorityResourcePool}
              firstTimeWorkingThisCustomer={firstTimeWorkingThisCustomer}
              gpsStatus={gpsStatus}
              employeePhoneNumber={employeePhoneNumber}
            />
          </Box>
        </Box>
        <Box display="flex" alignItems="center" flex={1} minWidth={0} justifyContent="flex-end">
          {children}
        </Box>
      </Box>
      {isWorkerReportOpen && (
        <WorkerReport
          reliabilityScore={displayedReliabilityScore}
          employeeId={employeeId}
          onAcceptApplication={onAcceptApplication}
          onDeclineApplication={onDeclineApplication}
        />
      )}
    </Box>
  );
};

RosterCardContainer.propTypes = {
  isFavorite: PropTypes.bool,
  reliabilityScore: PropTypes.number,
  rawReliability: PropTypes.number,
  rawAls: PropTypes.number,
  isPriorityResourcePool: PropTypes.bool,
  firstTimeWorkingThisCustomer: PropTypes.bool,
  gpsData: PropTypes.shape({
    onsite: PropTypes.bool,
    timestamp: PropTypes.number,
  }),
  gpsPings: PropTypes.arrayOf(
    PropTypes.shape({
      onsite: PropTypes.bool,
      timestamp: PropTypes.number,
    })
  ),
  children: PropTypes.node,
  employeeName: PropTypes.string,
  employeeId: PropTypes.string,
  agencyId: PropTypes.string,
  profilePictureSrc: PropTypes.string,
  avatarAlt: PropTypes.string,
  dataTestId: PropTypes.string,
  isWorkerReportOpen: PropTypes.bool,
  onAcceptApplication: PropTypes.func,
  onDeclineApplication: PropTypes.func,
  onFavoriteClick: PropTypes.func,
  employeePhoneNumber: PropTypes.string,
};

export default RosterCardContainer;
